import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  clearAllStorage,
  clearWithdrawnCategories,
  CookiePreferences,
  ConsentDecision,
  NO_PREFERENCES,
  persistConsent,
  publishConsentChange,
  readConsentDecision,
  readCookiePreferences,
} from '../../shared/utils/consentStorage';

interface CookieConsentContextType {
  cookieConsent: ConsentDecision;
  cookiePreferences: CookiePreferences;
  setCookieConsent: (consent: ConsentDecision, preferences?: CookiePreferences) => void;
  showBanner: boolean;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

interface CookieConsentProviderProps {
  children: ReactNode;
}

export const CookieConsentProvider: React.FC<CookieConsentProviderProps> = ({ children }) => {
  const [cookieConsent, setCookieConsentState] = useState<ConsentDecision>(null);
  const [cookiePreferences, setCookiePreferences] = useState<CookiePreferences>(NO_PREFERENCES);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const savedConsent = readConsentDecision();

    if (savedConsent) {
      setCookieConsentState(savedConsent);
      setCookiePreferences(readCookiePreferences());
      setShowBanner(false);
    } else {
      // No choice made yet - ask, and store nothing until there is an answer.
      setShowBanner(true);
    }
  }, []);

  const setCookieConsent = (consent: ConsentDecision, preferences?: CookiePreferences) => {
    setCookieConsentState(consent);

    if (consent === 'accepted') {
      const finalPreferences = preferences || NO_PREFERENCES;
      setCookiePreferences(finalPreferences);
      persistConsent(finalPreferences);
      // Accepting some categories is also a refusal of the others, so whatever
      // an earlier visit left behind for those has to go.
      clearWithdrawnCategories(finalPreferences);
    } else {
      // Declining leaves the device as clean as a first visit: the banner
      // reappears next time because nothing at all is kept.
      setCookiePreferences(NO_PREFERENCES);
      clearAllStorage();
    }

    // Lets the theme and language contexts store or drop their entries.
    publishConsentChange();
    setShowBanner(false);
  };

  return (
    <CookieConsentContext.Provider value={{
      cookieConsent,
      cookiePreferences,
      setCookieConsent,
      showBanner,
    }}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};
